import {
    SpaceBetween,
    BreadcrumbGroup,
    Flashbar,
    Header,
    Container,
    Form,
    FormField,
    Input,
    Button,
    Select,
} from '@amzn/awsui-components-console';
import RadioGroup from '@amzn/awsui-components-console/radio-group';
import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { displayErrorContent } from '../../../lib/utils';
import { SERVER_ERROR } from '../../../lib/constants';
import { configureLab } from '../apis';
import { validateAlarmRuleForm, isDuplicateAggregation, getMetricsForEntityType, getOperatorsForMetric, getAggregationsForMetric } from './alarmRuleUtils';
import {
    ENTITY_TYPES,
    NUMERIC_METRICS,
    DEVICE_METRICS,
    HOST_METRICS,
    OPERATORS,
    STATUS_OPERATORS,
    AGGREGATIONS,
    STATUS_AGGREGATIONS,
    UI_STRINGS,
} from './alarmRuleConstants';

// Find the { value, label } option object for a given value from a list
const findOption = (lists, value) => {
    for (const list of lists) {
        const found = list.find((item) => item.value === value);
        if (found) return found;
    }
    return value ? { value, label: value } : null;
};

export default function AlarmRuleForm(props) {
    const { id, ruleIndex } = useParams();
    const history = useHistory();
    const { state } = props.location;

    const isEditMode = ruleIndex !== undefined;
    const labId = state?.labId || id;
    const alarmRules = state?.alarmRules || [];
    const labData = state?.labData || {};
    const existingRule = isEditMode ? state?.rule : null;

    // Form field state
    const [entityType, setEntityType] = useState(() => {
        const raw = existingRule?.entityType || '';
        const match = ENTITY_TYPES.find((et) => et.value.toLowerCase() === raw.toLowerCase());
        return match ? match.value : raw;
    });
    const [metric, setMetric] = useState(
        existingRule ? findOption([DEVICE_METRICS, HOST_METRICS], existingRule.metric) : null
    );
    const [aggregation, setAggregation] = useState(
        existingRule ? findOption([AGGREGATIONS, STATUS_AGGREGATIONS], existingRule.aggregation) : null
    );
    const [operator, setOperator] = useState(
        existingRule ? findOption([OPERATORS, STATUS_OPERATORS], existingRule.operator) : null
    );
    const [threshold, setThreshold] = useState(
        existingRule?.threshold != null ? String(existingRule.threshold) : ''
    );

    // Validation error state
    const [entityTypeError, setEntityTypeError] = useState('');
    const [metricError, setMetricError] = useState('');
    const [aggregationError, setAggregationError] = useState('');
    const [operatorError, setOperatorError] = useState('');
    const [thresholdError, setThresholdError] = useState('');

    // UI state
    const [submitLoading, setSubmitLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState([]);

    // Redirect if labData is missing (e.g., direct URL access)
    useEffect(() => {
        if (!state || !state.labData || !state.labData.owner) {
            history.push('/lab-management/labs');
        }
    }, [state, history]);

    // Don't render form if labData is missing
    if (!state || !state.labData || !state.labData.owner) {
        return null;
    }

    const navigateBack = (updatedAlarmRules) => {
        const labState = {
            LabID: labId,
            owner: labData.owner,
            accessGroup: labData.accessGroup,
            dotsPoolName: labData.dotsPoolName,
            monitorEnabled: labData.monitorEnabled,
            Category: labData.ticketCategory,
            Type: labData.ticketType,
            Item: labData.ticketItem,
            Group: labData.ticketGroup,
            alarmRules: updatedAlarmRules || alarmRules,
        };
        history.push({
            pathname: `/lab-management/labs/configure/${labId}`,
            state: labState,
        });
    };

    const handleEntityTypeChange = ({ detail }) => {
        setEntityType(detail.value);
        setEntityTypeError('');
        setMetric(null);
        setMetricError('');
    };

    const handleSubmit = async () => {
        setErrorMessage([]);
        setEntityTypeError('');
        setMetricError('');
        setAggregationError('');
        setOperatorError('');
        setThresholdError('');

        const errors = validateAlarmRuleForm({
            entityType,
            metric,
            aggregation,
            operator,
            threshold,
        });

        if (Object.keys(errors).length > 0) {
            if (errors.entityType) setEntityTypeError(errors.entityType);
            if (errors.metric) setMetricError(errors.metric);
            if (errors.aggregation) setAggregationError(errors.aggregation);
            if (errors.operator) setOperatorError(errors.operator);
            if (errors.threshold) setThresholdError(errors.threshold);
            return;
        }

        const newRule = {
            entityType,
            metric: metric.value,
            aggregation: aggregation.value,
            operator: operator.value,
            threshold: isNumericMetric ? Number(threshold) : threshold.trim(),
        };

        const excludeIndex = isEditMode ? Number(ruleIndex) : -1;
        if (isDuplicateAggregation(newRule, alarmRules, excludeIndex)) {
            displayErrorContent(
                'Duplicate Aggregation',
                `A rule for this metric with the same aggregation already exists. Only one condition per aggregation is allowed.`,
                setErrorMessage
            );
            return;
        }

        const updatedRules = [...alarmRules];
        if (isEditMode) {
            const idx = Number(ruleIndex);
            if (!Number.isInteger(idx) || idx < 0 || idx >= alarmRules.length) {
                displayErrorContent('Client Error', 'Invalid rule index', setErrorMessage);
                return;
            }
            updatedRules[idx] = newRule;
        } else {
            updatedRules.push(newRule);
        }

        setSubmitLoading(true);
        try {
            await configureLab(
                labData.labId || labId,
                labData.owner,
                labData.accessGroup,
                labData.ticketCategory,
                labData.ticketType,
                labData.ticketItem,
                labData.ticketGroup,
                labData.dotsPoolName,
                labData.monitorEnabled,
                updatedRules
            );
            navigateBack(updatedRules);
        } catch (err) {
            const errResponse = err.response;
            if (errResponse && errResponse.data && errResponse.status === 400) {
                const errorException = errResponse.data.errorException || 'Client Error';
                const errMsg = errResponse.data.errorMessage || errResponse.data;
                displayErrorContent(errorException, errMsg, setErrorMessage);
            } else {
                displayErrorContent(
                    SERVER_ERROR,
                    UI_STRINGS.ERROR_SAVE,
                    setErrorMessage
                );
            }
        }
        setSubmitLoading(false);
    };

    const metricOptions = getMetricsForEntityType(entityType);
    const operatorOptions = getOperatorsForMetric(metric);
    const aggregationOptions = getAggregationsForMetric(metric);

    const isNumericMetric = metric && NUMERIC_METRICS.includes(metric.value);
    const thresholdDescription = !metric
        ? UI_STRINGS.SELECT_METRIC_FIRST
        : isNumericMetric
            ? UI_STRINGS.THRESHOLD_DESCRIPTION_NUMERIC
            : UI_STRINGS.THRESHOLD_DESCRIPTION_STATUS;
    const thresholdPlaceholder = !metric
        ? UI_STRINGS.SELECT_METRIC_FIRST
        : isNumericMetric
            ? UI_STRINGS.THRESHOLD_PLACEHOLDER_NUMERIC
            : UI_STRINGS.THRESHOLD_PLACEHOLDER_STATUS;

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#/' },
                    { text: 'Lab management', href: '#/lab-management/labs' },
                    { text: 'Configure', href: `#/lab-management/labs/configure/${labId}` },
                    { text: isEditMode ? UI_STRINGS.FORM_TITLE_EDIT : UI_STRINGS.FORM_TITLE_CREATE, href: '' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <form onSubmit={(e) => e.preventDefault()}>
                <Form
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button formAction="none" onClick={() => navigateBack()}>
                                {UI_STRINGS.CANCEL}
                            </Button>
                            <Button
                                formAction="none"
                                loading={submitLoading}
                                onClick={handleSubmit}
                                variant="primary"
                            >
                                {isEditMode ? UI_STRINGS.FORM_SAVE : UI_STRINGS.FORM_CREATE}
                            </Button>
                        </SpaceBetween>
                    }
                >
                    <SpaceBetween direction="vertical" size="m">
                        <Container
                            header={
                                <Header
                                    variant="h2"
                                    description={UI_STRINGS.DESCRIPTION}
                                >
                                    {isEditMode ? UI_STRINGS.FORM_TITLE_EDIT : UI_STRINGS.FORM_TITLE_CREATE}
                                </Header>
                            }
                        >
                            <SpaceBetween direction="vertical" size="m">
                                <FormField label={UI_STRINGS.ENTITY_TYPE_LABEL} description={UI_STRINGS.ENTITY_TYPE_DESCRIPTION} errorText={entityTypeError}>
                                    <RadioGroup
                                        value={entityType}
                                        onChange={handleEntityTypeChange}
                                        items={ENTITY_TYPES.map((et) => ({
                                            value: et.value,
                                            label: et.label,
                                            disabled: isEditMode,
                                        }))}
                                    />
                                </FormField>
                                <FormField label={UI_STRINGS.METRIC_LABEL} description={UI_STRINGS.METRIC_DESCRIPTION} errorText={metricError}>
                                    <Select
                                        selectedOption={metric}
                                        disabled={isEditMode}
                                        onChange={({ detail }) => {
                                            setMetric(detail.selectedOption);
                                            setMetricError('');
                                            setAggregation(null);
                                            setAggregationError('');
                                            setOperator(null);
                                            setOperatorError('');
                                        }}
                                        options={metricOptions}
                                        placeholder={UI_STRINGS.METRIC_PLACEHOLDER}
                                    />
                                </FormField>
                                <FormField label={UI_STRINGS.AGGREGATION_LABEL} description={UI_STRINGS.AGGREGATION_DESCRIPTION} errorText={aggregationError}>
                                    <Select
                                        selectedOption={aggregation}
                                        onChange={({ detail }) => {
                                            setAggregation(detail.selectedOption);
                                            setAggregationError('');
                                        }}
                                        options={aggregationOptions}
                                        placeholder={UI_STRINGS.AGGREGATION_PLACEHOLDER}
                                    />
                                </FormField>
                                <FormField label={UI_STRINGS.OPERATOR_LABEL} description={UI_STRINGS.OPERATOR_DESCRIPTION} errorText={operatorError}>
                                    <Select
                                        selectedOption={operator}
                                        onChange={({ detail }) => {
                                            setOperator(detail.selectedOption);
                                            setOperatorError('');
                                        }}
                                        options={operatorOptions}
                                        placeholder={UI_STRINGS.OPERATOR_PLACEHOLDER}
                                    />
                                </FormField>
                                <FormField label={UI_STRINGS.THRESHOLD_LABEL} description={thresholdDescription} errorText={thresholdError}>
                                    <Input
                                        value={threshold}
                                        onChange={({ detail }) => {
                                            setThreshold(detail.value);
                                            setThresholdError('');
                                        }}
                                        placeholder={thresholdPlaceholder}
                                    />
                                </FormField>
                            </SpaceBetween>
                        </Container>
                        <Flashbar items={errorMessage} />
                    </SpaceBetween>
                </Form>
            </form>
        </SpaceBetween>
    );
}
